import { computed } from '@preact/signals-react';
import type { AssistantActivity } from '@/embedded/chat/types/runtime-state';
import type {
  WebAgentAssistantActivity,
  WebAgentConversationParticipant,
} from '@/website/lib/chat-api';
import type { ActivityLiveDataset } from './activity-live-dataset';
import { isActiveAssistantActivityForSurface } from './activity-retention';
import { activityKey, normalizeText } from './conversation-utils';

type ConversationIdSignal = {
  value: string | null | undefined;
};

type ParticipantsSignal = {
  value: WebAgentConversationParticipant[];
};

export type ConversationParticipantActivity = {
  participant: WebAgentConversationParticipant;
  key: string;
  agentId: string;
  sessionId: string;
  rawActivity: WebAgentAssistantActivity;
  activity: AssistantActivity;
};

function assistantActivityFromAgentActivity(
  activity: WebAgentAssistantActivity | null
): AssistantActivity | null {
  if (!activity) return null;
  return {
    goal: activity.goal ?? undefined,
    action: activity.action ?? undefined,
    displayStrength: activity.displayStrength ?? undefined,
    displayPriority: activity.displayPriority ?? undefined,
    displayHoldMs: activity.displayHoldMs ?? undefined,
    phase: activity.phase,
    summary: activity.summary ?? undefined,
    status: activity.status ?? undefined,
    toolCallId: activity.toolCallId ?? undefined,
    toolName: activity.toolName ?? undefined,
    sequence: activity.sequence,
    updatedAt: activity.updatedAt,
    agentId: activity.agentId ?? undefined,
    sessionId: activity.sessionId ?? undefined,
    conversationId: activity.conversationId ?? undefined,
  };
}

function agentSessionParticipants(
  participants: WebAgentConversationParticipant[]
) {
  return participants.filter(participant => {
    return (
      normalizeText(participant.actorType) === 'agent_session' &&
      normalizeText(participant.agentId) !== null &&
      normalizeText(participant.sessionId) !== null
    );
  });
}

// salix is single-session: a conversation exposes one bare `agent` participant
// (no session_id — the single `im-<conversation_id>` session is implicit). When
// there are no willow-style `agent_session` participants, this is the one to
// correlate activity against, keyed by (agentId, conversationId).
function singleAgentParticipant(
  participants: WebAgentConversationParticipant[]
) {
  const agents = participants.filter(
    participant =>
      normalizeText(participant.actorType) === 'agent' &&
      normalizeText(participant.agentId) !== null
  );
  return agents.length === 1 ? agents[0] : null;
}

export class ConversationActivity {
  readonly participantActivities = computed<ConversationParticipantActivity[]>(
    () => {
      const conversationId = normalizeText(this.conversationId.value);
      if (!conversationId) return [];
      const activities: ConversationParticipantActivity[] = [];
      for (const participant of agentSessionParticipants(
        this.participants.value
      )) {
        const key = activityKey(participant.agentId, participant.sessionId);
        const agentId = normalizeText(participant.agentId);
        const sessionId = normalizeText(participant.sessionId);
        if (!key || !agentId || !sessionId) continue;
        const rawActivity = this.activityData.activityFor(conversationId, key);
        const activity = assistantActivityFromAgentActivity(rawActivity);
        if (
          !rawActivity ||
          !activity ||
          !isActiveAssistantActivityForSurface(activity)
        ) {
          continue;
        }
        activities.push({
          participant,
          key,
          agentId,
          sessionId,
          rawActivity,
          activity,
        });
      }

      // salix single-session fallback: no `agent_session` participants, so
      // correlate the conversation's single bare `agent` participant to its
      // activity by conversation (the activity carries the real conversationId).
      if (activities.length === 0) {
        const participant = singleAgentParticipant(this.participants.value);
        const agentId = participant && normalizeText(participant.agentId);
        if (participant && agentId) {
          for (const rawActivity of this.activityData
            .activitiesForConversation(conversationId)
            .values()) {
            if (normalizeText(rawActivity.agentId) !== agentId) continue;
            const activity = assistantActivityFromAgentActivity(rawActivity);
            if (!activity || !isActiveAssistantActivityForSurface(activity)) {
              continue;
            }
            const sessionId = normalizeText(rawActivity.sessionId);
            activities.push({
              participant,
              key: activityKey(agentId, sessionId) ?? agentId,
              agentId,
              sessionId: sessionId ?? '',
              rawActivity,
              activity,
            });
            break;
          }
        }
      }

      return activities;
    }
  );

  readonly rawActivities = computed(() =>
    this.participantActivities.value.map(item => item.rawActivity)
  );
  readonly displayedAssistantActivities = computed(() =>
    this.participantActivities.value.map(item => item.activity)
  );

  readonly hasActiveAssistantActivity = computed(() =>
    this.displayedAssistantActivities.value.length > 0
  );

  constructor(
    private readonly conversationId: ConversationIdSignal,
    private readonly participants: ParticipantsSignal,
    private readonly activityData: ActivityLiveDataset
  ) {}
}
